// React Dependencies
import React, { useState, useReducer } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, TextField, Select, MenuItem, InputAdornment, IconButton } from '@material-ui/core';
import { Visibility as VisibleIcon, VisibilityOff as InvisibleIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../components/Common/MText';
import MButton from '../../../components/MButton';
import Colours from '../../../styles/colours';
import OverLoading from '../../../components/Loading';
import ApiService from '../../../services/apiService';
import { Error, GroundStaffType } from '../../../common/constants';
import { containerHeaderTextStyle } from '../../../styles/common';
import { IfFalseThrowError, IsEmpty } from '../../../common/checks';
import { Trim } from '../../../common/util';

const Fields = { name: 'name', type: 'type', password: 'password' };
const FormContext = { Create: 'Create', Update: 'Update', UpdatePassword: 'UpdatePassword' };
const DialogSettings = {
  [FormContext.Create]: {
    title:          () => 'New Ground Staff',
    fields:         [Fields.name, Fields.type, Fields.password],
    action:         'Create Ground Staff',
    successMessage: 'Ground staff has been created',
  },
  [FormContext.Update]: {
    title:          (name) => `Ground staff ${name}`,
    fields:         [Fields.name, Fields.type],
    action:         'Update Ground Staff',
    successMessage: 'Ground staff has been updated',
  },
  [FormContext.UpdatePassword]: {
    title:          (name) => `Update password for ground staff ${name}`,
    fields:         [Fields.password],
    action:         'Update Password',
    successMessage: 'Password has been updated',
  },
};

const GroundStaffFormModal = ({ open, onClose, context, groundStaff, pageId, handleReload, handleLimitExceeded }) => {
  const settings = DialogSettings[context];

  const [isLoading, setIsLoading] = useState(false);
  const [isShowingPassword, setIsShowingPassword] = useState(false);
  const [form, setForm] = useReducer((state, action) => ({ ...state, ...action }), groundStaff);

  const validatePassword = () => {
    const password = Trim(form.password);
    return !IsEmpty(password) && password.length >= 4;
  };

  const onChange = (e) => {
    let { name, value } = e.target;
    if (name === Fields.password) {
      value = Trim(value, true);
    }
    setForm({ [name]: value });
  };

  const closeModal = () => {
    setForm({});
    onClose();
  };

  const onCreateSubmit = async () => {
    try {
      setIsLoading(true);
      const { name, type, password } = form;

      IfFalseThrowError(validatePassword(), 'Password is invalid');

      await ApiService.createGroundStaff(pageId, {
        name:     Trim(name, false, true),
        password: Trim(password),
        type,
      });
      toast.success(settings.successMessage);
      handleReload();
      closeModal();
    } catch (err) {
      if (err.message === Error.MaxNumberGroundStaffs) {
        closeModal();
        handleLimitExceeded();
      } else {
        toast.error(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdateSubmit = async () => {
    try {
      setIsLoading(true);
      const { id, name, type, password } = form;

      let updateDict;
      if (context === FormContext.UpdatePassword) {
        IfFalseThrowError(validatePassword(), 'Password is invalid');
        updateDict = { password: Trim(password) };
      } else {
        updateDict = { name: Trim(name, false, true), type };
      }

      await ApiService.updateGroundStaff(pageId, id, updateDict);
      toast.success(settings.successMessage);
      handleReload();
      closeModal();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = () => {
    if (context === FormContext.Create) {
      onCreateSubmit();
    } else {
      onUpdateSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={closeModal} fullWidth PaperProps={{ style: { padding: '10px' } }}>
      {isLoading && <OverLoading />}
      <DialogTitle>
        <MText
          bold
          text={settings.title(groundStaff?.name)}
          colour="inherit"
          size={containerHeaderTextStyle.fontSize}
        />
      </DialogTitle>
      <DialogContent>
        {
          settings.fields.includes(Fields.name) && (
            <div className="w-100 d-flex flex-column align-items-start mb-4">
              <MText text="Name" size="14px" className="mb-2" />
              <TextField variant="outlined" name={Fields.name} value={form.name} onChange={onChange} size="medium" className="w-100" />
            </div>
          )
        }

        {
          settings.fields.includes(Fields.type) && (
            <div className="w-100 d-flex flex-column align-items-start mb-4">
              <MText text="Type" size="14px" className="mb-2" />
              <Select variant="outlined" name={Fields.type} value={form.type} onChange={onChange} className="w-100">
                <MenuItem value="">-</MenuItem>
                {Object.values(GroundStaffType).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
              </Select>
            </div>
          )
        }

        {
          settings.fields.includes(Fields.password) && (
            <div className="w-100 d-flex flex-column align-items-start mb-4">
              <MText text="Password" size="14px" className="mb-2" />
              <TextField
                type={isShowingPassword ? 'text' : 'password'}
                variant="outlined"
                name={Fields.password}
                value={form.password}
                onChange={onChange}
                size="medium"
                className="w-100 pr-0"
                helperText={!validatePassword() ? 'Password must be at least 4 characters' : ''}
                FormHelperTextProps={{ style: { marginLeft: 0, color: Colours.Red } }}
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setIsShowingPassword(!isShowingPassword)} onMouseDown={(e) => e.preventDefault()}>
                        {isShowingPassword ? <VisibleIcon /> : <InvisibleIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )
        }

        <div className="d-flex justify-content-end align-items-center" style={{ gap: 10 }}>
          <MButton variant="outlined" text="Cancel" className="py-2 px-4" textSize="14px" onClick={closeModal} />
          <MButton
            variant="contained"
            text={settings.action}
            className="py-2 px-4"
            colour={Colours.Primary}
            textColour={Colours.White}
            textSize="14px"
            onClick={onSubmit}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

GroundStaffFormModal.propTypes = {
  open:                PropTypes.bool,
  onClose:             PropTypes.func,
  context:             PropTypes.oneOf(Object.keys(DialogSettings)),
  groundStaff:         PropTypes.shape(Object),
  pageId:              PropTypes.number,
  handleReload:        PropTypes.func,
  handleLimitExceeded: PropTypes.func,
};

GroundStaffFormModal.defaultProps = {
  open:                false,
  onClose:             () => {},
  context:             'Create',
  groundStaff:         {},
  pageId:              null,
  handleReload:        () => {},
  handleLimitExceeded: () => {},
};

export default GroundStaffFormModal;
